import PanelSelection from '../widgets/panelCustomisation/types/PanelSelection';
import { BehaviorCallback, BehaviorParameters } from '../../@types';
import getPanelArrangement from '../widgets/panelCustomisation/utils/getPanelArrangement';
import panelLayoutConfig from '../widgets/panelCustomisation/panelLayoutConfig';
import getPanelProductSelectionIds from '../widgets/panelCustomisation/utils/getPanelProductSelectionIds';
import applyPanelLayout from '../widgets/panelCustomisation/utils/applyPanelLayout';
import { CustomSlidingDoorComponent } from '@inter-ikea-storageone/range-pax';
import { CoreApi, Product, ProductType } from '@inter-ikea-storageone/range';
import { Entity } from '@inter-ikea-core/core';

/**
 * For a secified panel find the corresponding panel that fits the specified
 * sliding door
 */
const getCorrespondingPanelForSlidingDoor = (
  panel: Product,
  slidingDoor: Entity,
  allProducts: Product[],
  coreApi: CoreApi
) => {
  const panelOnSlidingDoor = coreApi.entities.getDescendantsWithProductType(
    slidingDoor,
    ProductType.PAX_SLIDING_DOOR_PANEL
  )[0];
  const panelSize = panelOnSlidingDoor && coreApi.entities.getEntityComSize(panelOnSlidingDoor);
  if (!panelSize) {
    return null;
  }
  const isRotated = panel.coreProductId.includes('rotated');
  const correspondingPanels = allProducts.filter(
    product =>
      product.type === ProductType.PAX_SLIDING_DOOR_PANEL &&
      product.design === panel.design &&
      product.name === panel.name &&
      product.communicatedDimensions.width === panelSize.width &&
      product.communicatedDimensions.height === panelSize.height
  );

  return isRotated
    ? correspondingPanels.find(p => p.coreProductId.includes('rotated'))
    : correspondingPanels.find(p => !p.coreProductId.includes('rotated'));
};

const replaceSlidingDoorPanel: (
  panelSelection: PanelSelection
) => BehaviorCallback = panelSelection => ({ coreApi, props, rootState }: BehaviorParameters) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const panelProduct = rootState.products.inMarket.find(
    p => p.coreProductId === props.coreProductId
  );
  if (!selectedEntity || !panelProduct) {
    return;
  }

  const customSlidingDoors = coreApi.entities.getByComponent(CustomSlidingDoorComponent.id);
  const slidingDoors = customSlidingDoors.length > 1 ? customSlidingDoors : [selectedEntity];
  // For custom sliding doors we change panels for all at once
  slidingDoors.forEach(door => {
    const panel =
      customSlidingDoors.length > 1
        ? getCorrespondingPanelForSlidingDoor(
            panelProduct,
            door,
            rootState.products.inMarket,
            coreApi
          )
        : panelProduct;
    const panelArrangement = getPanelArrangement(door, coreApi);
    const layout = panelLayoutConfig.find(
      panelLayout => panelLayout.arrangement === panelArrangement
    );
    const panelProductSelectionIds = getPanelProductSelectionIds(door, coreApi);
    if (layout && panelProductSelectionIds && panel) {
      panelProductSelectionIds[panelSelection] = panel.coreProductId;
      applyPanelLayout(
        door,
        layout,
        panelProductSelectionIds.primary,
        panelProductSelectionIds.secondary,
        coreApi
      );
    }
  });
};

export default replaceSlidingDoorPanel;
